import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Divider,
  Alert,
  FormHelperText,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Avatar,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  LocalOffer as OfferIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  CreditCard as PaymentIcon,
  Redeem as RedeemIcon,
  ShoppingCart as CartIcon,
  InfoOutlined as InfoIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Custom styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const SummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  },
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.primary.main, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const RedeemItemModal = ({ open, onClose, item, onSubmit, walletBalance }) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // For physical items that need shipping
  const [shippingDetails, setShippingDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phoneNumber: ''
  });

  const handleShippingDetailsChange = (field) => (event) => {
    setShippingDetails({
      ...shippingDetails,
      [field]: event.target.value
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate current step's fields
    if (currentStep === 0) {
      // Validate quantity
      if (quantity < 1) {
        newErrors.quantity = 'Quantity must be at least 1';
      }
      
      if (item.quantity && quantity > item.quantity) {
        newErrors.quantity = `Only ${item.quantity} items available`;
      }
      
      // Check if user has enough tokens
      if (walletBalance < (item.tokenPrice * quantity)) {
        newErrors.wallet = `Insufficient tokens. You need ${item.tokenPrice * quantity} tokens`;
      }
    }
    
    // Only validate shipping details on shipping step for physical items
    if (currentStep === 1 && item.isPhysical) {
      if (!shippingDetails.name) newErrors.name = 'Name is required';
      if (!shippingDetails.address) newErrors.address = 'Address is required';
      if (!shippingDetails.city) newErrors.city = 'City is required';
      if (!shippingDetails.state) newErrors.state = 'State/Province is required';
      if (!shippingDetails.zipCode) newErrors.zipCode = 'ZIP/Postal code is required';
      if (!shippingDetails.country) newErrors.country = 'Country is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      try {
        await onSubmit({
          itemId: item._id,
          quantity,
          shippingDetails: item.isPhysical ? shippingDetails : null,
          notes
        });
        setSuccess(true);
        setTimeout(() => {
          onClose();
          // Reset form values
          setQuantity(1);
          setNotes('');
          setShippingDetails({
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
            phoneNumber: ''
          });
          setCurrentStep(0);
          setSuccess(false);
        }, 2000);
      } catch (error) {
        console.error("Redemption error:", error);
        setErrors({ submit: error.message || 'Failed to complete redemption' });
      } finally {
        setLoading(false);
      }
    }
  };

  const totalCost = quantity * item.tokenPrice;
  const remainingBalance = walletBalance - totalCost;
  
  const steps = [
    { label: 'Item & Quantity', icon: <CartIcon /> },
    item.isPhysical ? { label: 'Shipping', icon: <ShippingIcon /> } : { label: 'Details', icon: <InfoIcon /> },
    { label: 'Confirm', icon: <ReceiptIcon /> }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              mr: 2, 
              color: 'white',
            }}
          >
            <RedeemIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Redeem: {item?.name}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'white' }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      
      <Box sx={{ position: 'relative' }}>
        <CircleDecoration size={200} top="-100px" right="-100px" opacity={0.03} />
        <CircleDecoration size={150} bottom="-50px" left="-50px" opacity={0.03} />
      
        <DialogContent sx={{ p: 3, position: 'relative' }}>
          {success ? (
            <Box 
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                py: 4 
              }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.success.main,
                  width: 80,
                  height: 80,
                  mb: 2,
                  boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0.2)}`
                }}
              >
                <CheckIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Redemption Successful!
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 400, mb: 2 }}>
                Your redemption for {quantity} {quantity > 1 ? 'items' : 'item'} has been processed successfully.
              </Typography>
              <Chip 
                icon={<OfferIcon />}
                label={`${totalCost} tokens redeemed`}
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          ) : (
            <>
              <Stepper 
                activeStep={currentStep} 
                alternativeLabel 
                sx={{ 
                  mb: 4,
                  '& .MuiStepLabel-label': {
                    mt: 1
                  }
                }}
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel StepIconProps={{ 
                      icon: step.icon,
                      sx: { 
                        color: currentStep >= index 
                          ? theme.palette.primary.main 
                          : theme.palette.grey[400]
                      }
                    }}>
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {currentStep === 0 && (
                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                  <Grid container spacing={3}>
                    {/* Item Information */}
                    <Grid item xs={12} md={6}>
                      <ContentSection>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                          <InfoIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main }} />
                          Item Details
                        </Typography>
                        
                        <Box sx={{ display: 'flex', mb: 2, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                          {item.imageUrl ? (
                            <Box 
                              component="img" 
                              src={item.imageUrl}
                              alt={item.name}
                              sx={{ 
                                width: '100%', 
                                height: 160, 
                                objectFit: 'cover',
                                borderRadius: 2 
                              }}
                            />
                          ) : (
                            <Box 
                              sx={{ 
                                width: '100%', 
                                height: 160, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderRadius: 2
                              }}
                            >
                              <RedeemIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3) }} />
                            </Box>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Chip 
                            icon={<OfferIcon />} 
                            label={`${item.tokenPrice} tokens per item`}
                            color="primary"
                            sx={{ mr: 1, fontWeight: 'bold' }}
                          />
                          {item.category && (
                            <Chip label={item.category} size="small" variant="outlined" />
                          )}
                        </Box>
                        
                        <Typography variant="body1" paragraph>
                          {item.description}
                        </Typography>
                        
                        {item.quantity !== undefined && item.quantity <= 5 && (
                          <Alert 
                            severity="warning" 
                            icon={<WarningIcon />}
                            sx={{ mb: 2, borderRadius: 2 }}
                          >
                            Only {item.quantity} items left in stock!
                          </Alert>
                        )}
                      </ContentSection>
                    </Grid>
                    
                    {/* Quantity Selection */}
                    <Grid item xs={12} md={6}>
                      <ContentSection>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                          <CartIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main }} />
                          Quantity & Cost
                        </Typography>
                        
                        <TextField
                          label="Quantity"
                          type="number"
                          fullWidth
                          value={quantity}
                          onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                          InputProps={{ 
                            inputProps: { min: 1, max: item.quantity },
                            startAdornment: (
                              <InputAdornment position="start">
                                <CartIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                          error={!!errors.quantity}
                          helperText={errors.quantity}
                          sx={{ mb: 3 }}
                        />
                        
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            mb: 3, 
                            bgcolor: alpha(theme.palette.background.paper, 0.5),
                            borderRadius: 2 
                          }}
                        >
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Cost Summary:
                          </Typography>
                          
                          <SummaryItem>
                            <Typography variant="body2">Item Cost:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.tokenPrice} × {quantity} = {totalCost} tokens
                            </Typography>
                          </SummaryItem>
                          
                          <SummaryItem>
                            <Typography variant="body2">Your Balance:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {walletBalance} tokens
                            </Typography>
                          </SummaryItem>
                          
                          <SummaryItem>
                            <Typography variant="body2">Remaining Balance:</Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: remainingBalance < 0 ? theme.palette.error.main : 'inherit'
                              }}
                            >
                              {remainingBalance} tokens
                            </Typography>
                          </SummaryItem>
                        </Paper>
                        
                        {errors.wallet && (
                          <Alert 
                            severity="error" 
                            sx={{ mb: 2, borderRadius: 2 }}
                          >
                            {errors.wallet}
                          </Alert>
                        )}
                        
                        <TextField
                          label="Notes (Optional)"
                          multiline
                          rows={3}
                          fullWidth
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Special requests or comments about your redemption..."
                          sx={{ mb: 2 }}
                        />
                      </ContentSection>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {currentStep === 1 && item.isPhysical && (
                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <ShippingIcon sx={{ mr: 1 }} />
                    Shipping Details
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Please provide your shipping information for this physical item.
                  </Alert>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Full Name"
                        fullWidth
                        value={shippingDetails.name}
                        onChange={handleShippingDetailsChange('name')}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label="Address"
                        fullWidth
                        value={shippingDetails.address}
                        onChange={handleShippingDetailsChange('address')}
                        error={!!errors.address}
                        helperText={errors.address}
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="City"
                        fullWidth
                        value={shippingDetails.city}
                        onChange={handleShippingDetailsChange('city')}
                        error={!!errors.city}
                        helperText={errors.city}
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="State/Province"
                        fullWidth
                        value={shippingDetails.state}
                        onChange={handleShippingDetailsChange('state')}
                        error={!!errors.state}
                        helperText={errors.state}
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="ZIP/Postal Code"
                        fullWidth
                        value={shippingDetails.zipCode}
                        onChange={handleShippingDetailsChange('zipCode')}
                        error={!!errors.zipCode}
                        helperText={errors.zipCode}
                        required
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={!!errors.country} sx={{ mb: 2 }}>
                        <InputLabel id="country-select-label">Country</InputLabel>
                        <Select
                          labelId="country-select-label"
                          value={shippingDetails.country}
                          onChange={handleShippingDetailsChange('country')}
                          label="Country"
                          required
                        >
                          <MenuItem value="US">United States</MenuItem>
                          <MenuItem value="CA">Canada</MenuItem>
                          <MenuItem value="UK">United Kingdom</MenuItem>
                          <MenuItem value="AU">Australia</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        value={shippingDetails.phoneNumber}
                        onChange={handleShippingDetailsChange('phoneNumber')}
                        placeholder="For delivery questions"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {currentStep === 1 && !item.isPhysical && (
                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    Redemption Details
                  </Typography>
                  
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    This is a digital item and will be delivered to your account after redemption.
                  </Alert>
                  
                  <Typography variant="body1" paragraph>
                    Please review your redemption information:
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Item:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                      {item.name}
                    </Typography>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Quantity:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                      {quantity}
                    </Typography>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Total Cost:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2, color: theme.palette.primary.main }}>
                      {totalCost} tokens
                    </Typography>
                    
                    {notes && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          Your Notes:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                          {notes}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              )}
              
              {currentStep === 2 && (
                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <ReceiptIcon sx={{ mr: 1 }} />
                    Confirm Redemption
                  </Typography>
                  
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3, 
                      mb: 3, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03)
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          Item
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {item.name}
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                          Quantity
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {quantity}
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                          Type
                        </Typography>
                        <Chip 
                          label={item.isPhysical ? 'Physical Item' : 'Digital Item'} 
                          size="small"
                          color={item.isPhysical ? 'secondary' : 'primary'}
                          icon={item.isPhysical ? <ShippingIcon /> : <InfoIcon />}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          Cost
                        </Typography>
                        <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                          {totalCost} tokens
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                          Balance After Redemption
                        </Typography>
                        <Typography 
                          variant="body1" 
                          gutterBottom 
                          sx={{ 
                            fontWeight: 'bold',
                            color: remainingBalance < 0 ? 'error.main' : 'success.main' 
                          }}
                        >
                          {remainingBalance} tokens
                        </Typography>
                      </Grid>
                      
                      {item.isPhysical && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom color="text.secondary">
                            Shipping Address
                          </Typography>
                          <Typography variant="body2">
                            {shippingDetails.name}<br />
                            {shippingDetails.address}<br />
                            {shippingDetails.city}, {shippingDetails.state} {shippingDetails.zipCode}<br />
                            {shippingDetails.country}
                            {shippingDetails.phoneNumber && (
                              <><br />Phone: {shippingDetails.phoneNumber}</>
                            )}
                          </Typography>
                        </Grid>
                      )}
                      
                      {notes && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom color="text.secondary">
                            Notes
                          </Typography>
                          <Typography variant="body2">
                            {notes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                  
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    This action will deduct {totalCost} tokens from your balance. This cannot be undone.
                  </Alert>
                  
                  {errors.submit && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {errors.submit}
                    </Alert>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, position: 'relative' }}>
          {!success && (
            <>
              <Button 
                onClick={onClose} 
                color="inherit"
                disabled={loading}
              >
                Cancel
              </Button>
              
              {currentStep > 0 && (
                <Button 
                  onClick={handleBack} 
                  disabled={loading}
                >
                  Back
                </Button>
              )}
              
              {currentStep < 2 ? (
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={loading}
                >
                  Next
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading || !item || walletBalance < (item.tokenPrice * quantity)}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                >
                  {loading ? 'Processing...' : 'Confirm Redemption'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default RedeemItemModal;